/**
 * Final submission step: merged clinical PDF preview, attachment management,
 * NCCN guidelines viewer, fax branch, and form submit for a single order.
 *
 * @module finalDocuments/finalDocuments
 */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, openModal } from "risa-oasis-ui_v2";
import DocumentsModal from "../../components/modals/documentsModal/documentsModal";
import DocViewerWithSideNavigation from "../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../enums/eventsData";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../enums/modalId";
import { resetFileUploadState } from "../../redux/slice/fileUploadSlice";
import { fetchNccnGuidelinesDocuments } from "../../redux/slice/medicalPa/guidelinesDocumentsSlice";
import { closeModal, setOpenedModalId } from "../../redux/slice/modalSlice";
import { fetchWorkflowConfigDataForAnOrder } from "../../redux/slice/workflowConfigSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { filledPdfLinkFromSubmissionOutput } from "../../utils/getFaxPdfLinkRecursively";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { resolveFaxFormId } from "../procedureQuestionaire/utils/faxFormIdResolver";
import { getAllJCodesFromAuthOnFile } from "../singleOrderLayout/utils/existingDocsToLs";
import { FinalDocumentsPdfPanel } from "./components/FinalDocumentsPdfPanel";
import {
  useDocumentManagement,
  useFaxSubmission,
  useFinalDocumentsData,
  useFormSubmission,
  useLocalDocumentPageMapping,
  useMergePdfs,
} from "./hooks";
import {
  logManageAttachmentsClick,
  logSubmissionInitiated,
  logSubmitFormClick,
  logUploadingAttachmentCompleted,
} from "./utils/bigQueryLogger";
import { handleFaxSubmission } from "./utils/handleFaxSubmission";

/**
 * Route page that loads order + guidelines, manages PDFs/modals, and submits the final packet.
 *
 * @returns Full-page layout with PDF viewer, actions, and conditional modals
 */
const FinalDocuments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: guidelinesDocuments, loading: isGuidelinesDocumentsLoading } =
    useSelector((state: RootState) => state.guidelinesDocuments);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const orgId = getOrgIdForFetchExternalWorklist();

  // Redux selectors
  const { openedModalId, user, downloadURL, progress } =
    useFinalDocumentsData();

  // Postgres-backed order data (source for the whole page)
  const {
    generalOrderDetails,
    generalOrderDetailsLoading,
    generalOrderDetailsError,
    authOnFileDetails,
    coverageDetails,
    submissionData,
    fetchAll,
  } = useDataAccumulatorForSingleOrder();

  // Derived, primitive values pulled from the Postgres accumulator domains
  const order = generalOrderDetails?.order ?? null;
  const authOnFile = authOnFileDetails?.auth_on_file ?? null;
  const patientId = order?.demographics?.patient_id ?? "";
  const submissionStatus = order?.status?.submission_status ?? "";
  const financialReviewStatus = order?.status?.financial_review ?? "";
  const authStatus = authOnFile?.auth_status ?? "";
  const regimenName = order?.payload?.regimen_name ?? "";
  const selectedFormId = resolveFaxFormId(submissionData?.submission);
  /* `location.state.faxPdfLink` dies on refresh, so the filled fax PDF is also
   * read from Postgres to keep the preview on the submitted form instead of
   * dropping back to the clinical document.
   */
  const filledPdfPath =
    filledPdfLinkFromSubmissionOutput(
      submissionData?.submission?.submission_output,
    ) ?? null;
  /* Fax preview is the filled form, not the clinical packet, so
   * `document_page_mapping` (clinical page ranges) is not shown. Include
   * `filledPdfPath` so a refresh still hides mappings after router state is
   * gone.
   */
  const isFaxFlow = Boolean(location.state?.faxPdfLink || filledPdfPath);
  const orderDocumentPageMapping =
    order?.clinical_document?.document_page_mapping;
  const primaryPayerName =
    coverageDetails?.coverages?.find(
      (coverage) => coverage.coverage_type === "primary",
    )?.payer_name ?? "";
  const jCodesCsv =
    getAllJCodesFromAuthOnFile(authOnFile ?? undefined)
      ?.map((jCode) => jCode?.j_code ?? "")
      .join(",") ?? "";
  const validHpcsCodesCsv =
    order?.status?.meta?.hpcs_codes
      ?.filter(
        (hpcs) => hpcs.auth_status === MedicalPaOrdersAuthStatus.AuthRequired,
      )
      ?.map((hpcs) => hpcs.j_code)
      ?.join(",") ?? "";

  // Document management hook
  const {
    patientName,
    isLoading,
    hasResolvedDocument,
    fileNames,
    documents,
    setFileNames,
    setDocuments,
    downloadPdf,
  } = useDocumentManagement({
    order,
    isLoadingOrder: generalOrderDetailsLoading,
    orderId: id ?? "",
    filledPdfPath,
  });

  /* Covers the whole order → document chain so the viewer never flashes its
   * "no clinical document" state before the first fetch has even started. Only
   * a failed order fetch releases the loader with nothing resolved.
   */
  const isDocumentPending =
    isLoading ||
    generalOrderDetailsLoading ||
    (!hasResolvedDocument && !generalOrderDetailsError);

  // PDF merge hook - merges multiple PDFs into one
  const { mergedFile, isMerging, mergePdfsFromBase64 } = useMergePdfs();

  const { documentPageMapping, mappingDirty, handleMergePdfs } =
    useLocalDocumentPageMapping({
      orderMapping: orderDocumentPageMapping,
      isOrderLoaded: Boolean(order) && !generalOrderDetailsLoading,
      mergePdfsFromBase64,
      previousFile: mergedFile ?? documents[0] ?? null,
    });

  // Fax submission hook
  const { makeSendCallAndUpdateFirestore } = useFaxSubmission({
    orderId: id,
    faxSubmissionStatus: order?.status?.submission_status ?? "",
    orgId: orgId ?? "",
  });

  // Form submission hook
  const {
    isSubmitting,
    setIsSubmitting,
    updateClinicalDocument,
    submitNormalFlow,
    finalButtonText: normalFlowButtonText,
  } = useFormSubmission({
    submissionStatus,
    orderId: id,
    patientId,
    documents,
    downloadURL: downloadURL ?? null,
    progress,
    documentPageMapping,
    mappingDirty,
  });

  // Fetch order details on mount
  useEffect(() => {
    if (id) {
      dispatch(fetchNccnGuidelinesDocuments({ order_id: id }));
      dispatch(fetchWorkflowConfigDataForAnOrder(id));
      fetchAll(id, orgId ?? "", { force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  // Clear stale downloadURL so remount does not re-trigger submission
  useEffect(() => {
    return () => {
      dispatch(resetFileUploadState());
    };
  }, [dispatch]);

  // Handle file upload completion
  useEffect(() => {
    if (downloadURL) {
      logUploadingAttachmentCompleted({
        patientId,
        orderId: id ?? "",
        payerName: primaryPayerName,
        regimenName,
        orgId: orgId ?? "",
        userId: user?.id ?? "",
        userEmail: user?.email ?? "",
      });

      if (location.state?.faxPdfLink) {
        handleFaxSubmission(
          id ?? "",
          location.state.faxPdfLink,
          selectedFormId,
          setIsSubmitting,
          navigate,
          makeSendCallAndUpdateFirestore,
        );
      } else {
        updateClinicalDocument();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadURL]);

  // Handle modal opening
  useEffect(() => {
    if (openedModalId === ModalId.FINAL_DOCUMENT_MODAL) {
      openModal(ModalId.FINAL_DOCUMENT_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
  }, [openedModalId]);

  /**
   * Handles the primary submit button click.
   * Logs the submit interaction to BigQuery and runs the normal
   * upload-then-submit flow via `submitNormalFlow`.
   */
  const handleSubmitClick = async () => {
    logSubmitFormClick({
      patientId,
      orderId: id ?? "",
      payerName: primaryPayerName,
      regimenName,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        form_id: selectedFormId,
        status: submissionStatus,
        j_codes: jCodesCsv,
        valid_hpcs_codes: validHpcsCodesCsv,
      },
    });

    logSubmissionInitiated({
      patientId,
      orderId: id ?? "",
      payerName: primaryPayerName,
      regimenName,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        payer_name: primaryPayerName,
        regimen_name: regimenName,
        form_id: selectedFormId,
        status: submissionStatus,
        j_codes: jCodesCsv,
        valid_hpcs_codes: validHpcsCodesCsv,
      },
    });

    await submitNormalFlow();
  };

  /** Opens the manage-attachments modal and logs the interaction to BigQuery. */
  const handleManageAttachmentsClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.FINAL_DOCUMENT_MODAL }));

    logManageAttachmentsClick({
      patientId,
      orderId: id ?? "",
      payerName: primaryPayerName,
      regimenName,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        order_status: financialReviewStatus,
        auth_status: authStatus,
      },
    });
  };

  /** Opens the NCCN guidelines viewer modal. */
  const handleViewGuidelinesClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION }));
  };

  return (
    <div className="flex h-full w-screen flex-col items-center justify-center bg-primaryGray-16 p-2">
      <div className="flex h-full w-1/2 flex-1 flex-col gap-2 overflow-y-hidden rounded-md bg-white p-4">
        <div className="flex w-full flex-row items-center gap-2 p-2">
          <div className="text-h16 flex items-center justify-center font-semibold">
            {patientName}
          </div>
          <div>
            (
            {order?.demographics?.patient_id ??
              order?.demographics?.patient_mrn ??
              ""}
            )
          </div>

          <div className="ml-auto flex items-center gap-4">
            <Button
              buttonType="secondary"
              size="small"
              disabled={false}
              onClick={handleViewGuidelinesClick}
            >
              View Guidelines
            </Button>
            <Button
              buttonType="primary"
              size="small"
              disabled={false}
              onClick={handleManageAttachmentsClick}
            >
              Manage Attachments
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <FinalDocumentsPdfPanel
            mergedFile={generalOrderDetailsLoading ? null : mergedFile}
            documents={documents ?? []}
            isLoading={isDocumentPending}
            documentPageMapping={isFaxFlow ? undefined : documentPageMapping}
          />
        </div>

        <div className="flex flex-row items-center justify-between py-2">
          <div
            className={`text-sm font-bold ${
              isDocumentPending
                ? "cursor-not-allowed text-primaryGray-12"
                : "text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-6"
            }`}
            onClick={isDocumentPending ? undefined : downloadPdf}
          >
            Save as Draft
          </div>
          <div className="actions-buttons flex gap-4">
            <Button
              disabled={false}
              children={"Go Back"}
              onClick={() => {
                navigate(-1);
              }}
              buttonType={"secondary"}
              size={"medium"}
            />
            <Button
              buttonType="primary"
              size="medium"
              disabled={isDocumentPending || isSubmitting}
              onClick={handleSubmitClick}
            >
              {normalFlowButtonText}
            </Button>
          </div>
        </div>
      </div>
      {openedModalId === ModalId.FINAL_DOCUMENT_MODAL && (
        <DocumentsModal
          onClose={() => dispatch(closeModal())}
          documentsName={fileNames}
          documentsList={documents}
          id={id ?? ""}
          newDocumentNames={(urls) => setFileNames(urls)}
          newFiles={(files) => setDocuments(files)}
          patientId={patientId}
          orderId={id ?? ""}
          orderData={{
            demographics: order?.demographics,
            auth_on_file: authOnFile ?? undefined,
          }}
          onMergePdfs={handleMergePdfs}
          isMerging={isMerging}
        />
      )}
      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          modalName={ModalNamesForBigQuery.NCCN_GUIDELINES_MODAL}
          items={guidelinesDocuments ?? []}
          showLoader={isGuidelinesDocumentsLoading}
          screenName={ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN}
          shouldLogEvent={true}
        />
      )}
    </div>
  );
};

export default FinalDocuments;
